import { fuzzySearch } from '@/utils/fuzzySearch';
import {
    Autocomplete,
    AutocompleteRenderGroupParams,
    Box,
    Button,
    FormControl,
    FormHelperText,
    SxProps,
    TextField,
} from '@mui/material';
import _ from 'lodash';
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { AxleTheme } from '../../theme';
import {
    AllowedInputValue,
    AllowedInputValueItem,
    InputState,
    polymorphicIsEqual,
} from '../../utils/inputState';
import { Children, ReturnComponent, Style } from '../../utils/reactTypes';
import { If } from '../utils/conditionalComponents';
import { FieldWithAxleLabel, labelWithRequiredAsterisk } from './AxleLabel';

// Sentinel values for buttons/labels - we use these so there is no possibility of
// a collision with actual options in the list
const SHOW_MORE_SENTINEL = Symbol('SHOW_MORE_BUTTON');
const DEFAULT_OPTIONS_SENTINEL = Symbol('DEFAULT_OPTIONS');
const MORE_OPTIONS_SENTINEL = Symbol('MORE_OPTIONS_SENTINEL');
const SENTINELS = {
    [SHOW_MORE_SENTINEL]: true,
    [DEFAULT_OPTIONS_SENTINEL]: true,
    [MORE_OPTIONS_SENTINEL]: true,
};

export interface AxleAutoCompleteFieldProps<
    InputValue extends AllowedInputValue<AllowedInputValueItem>,
    Option,
> {
    label?: string;
    inlineLabel?: boolean;
    enabled?: boolean;
    disabledHelperText?: string;
    hideHelperText?: boolean;
    inputState: InputState<InputValue>;
    options: Option[];
    moreOptions?: Option[];
    showMoreButtonText?: string;
    optionToValue: (option: Option) => InputValue;
    optionToLabel: (option: Option) => string;
    renderOption?: (option: Option) => ReturnComponent;
    groupBy?: (option: Option) => string;
    renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
    handleNullValue?: () => void;
    disableClearable?: boolean;
    sortOptions?: boolean;
    specialAutocompleteOnlyToAddMode?: boolean;
    filterOptions?: (options: Option[]) => Option[];
    style?: Style;
    tooltipText?: string;
    noOptionsText?: string;
    placeholder?: string;
    description?: Children;
    disableResetWithoutInput?: boolean;
    // Controls whether the controlled value should be displayed even if it's not
    // available in the options list. This behavior is natively supported by
    // Autocomplete when providing a `value` that's missing in the options list,
    // but our default wrapper behavior is to pass in `null` as the controlled
    // value in that case instead.
    //
    // Should only be set to `true` when the input value and options are of the
    // same type, otherwise, the value will fail to render.
    displayMissingOptionValue?: boolean;
    optionStyle?: SxProps<typeof AxleTheme>;
    _autocompleteRootInputStyles?: Style;
}

// Like <AxleSelectField/>, but lets the user type to filter the options.
// If you only have 3–4 fixed options, use <AxleSelectField/> instead.
//  Otherwise, for many options, use this.
export const AxleAutocompleteField = <
    Option,
    InputValue extends AllowedInputValue<AllowedInputValueItem>,
>({
    label = '',
    inlineLabel = false,
    enabled = true,
    tooltipText = '',
    disabledHelperText = '',
    hideHelperText = false,
    inputState: { value, required, setInputState, showError, helperText, setIsTouched, reset },
    options,
    moreOptions = [],
    showMoreButtonText = 'Show More',
    optionToValue,
    optionToLabel,
    renderOption = undefined,
    // Whether to show the “×” button to reset the input to its default value.
    disableClearable = false,
    handleNullValue = undefined,
    // Whether to sort the options.
    sortOptions = true,
    // Special flag used by <AxleMultiSelectWithChipsArray /> and
    //  other components that only use this field to add new items
    //  to a list, but don’t need this field to display a selected option.
    specialAutocompleteOnlyToAddMode = false,
    // Optional function to filter options down with a function.
    filterOptions = undefined,
    // Pass-through parameters.
    style = undefined,
    groupBy = undefined,
    renderGroup = undefined,
    noOptionsText = undefined,
    description = undefined,
    placeholder = 'Type to filter...',
    disableResetWithoutInput = false,
    displayMissingOptionValue = false,
    optionStyle = undefined,
    _autocompleteRootInputStyles: _extraAutocompleteStyles,
}: AxleAutoCompleteFieldProps<InputValue, Option>): ReturnComponent => {
    // Toggle to show more options when "Show More" is clicked
    const [showMore, setShowMore] = useState(false);

    const isSentinel = useCallback(
        (option: Option | null): boolean => Boolean(SENTINELS[option as keyof typeof SENTINELS]),
        [],
    );

    // Combine options based on whether "Show More" has been clicked
    const currentOptions = useMemo(() => {
        if (sortOptions) {
            const sortedInitialOptions = _.sortBy(options, optionToLabel);
            const sortedMoreOptions = _.sortBy(moreOptions, optionToLabel);
            return showMore
                ? [...sortedInitialOptions, ...sortedMoreOptions]
                : sortedInitialOptions;
        }
        return showMore ? [...options, ...moreOptions] : options;
    }, [moreOptions, optionToLabel, options, showMore, sortOptions]);

    const optionsProp = useMemo(() => {
        if (moreOptions.length === 0) {
            return currentOptions;
        }

        if (!showMore) {
            return currentOptions.concat([SHOW_MORE_SENTINEL as unknown as Option]);
        }
        return [
            DEFAULT_OPTIONS_SENTINEL,
            ...currentOptions.slice(0, options.length),
            MORE_OPTIONS_SENTINEL,
            ...currentOptions.slice(options.length),
        ];
    }, [currentOptions, moreOptions.length, options.length, showMore]);

    // If the options change, then mark the input as untouched, and reset the value.
    const [lastRecordedOptions, setLastRecordedOptions] = useState(currentOptions);
    useEffect(() => {
        // Comparing arrays in Javascript doesn't work by itself.
        if (polymorphicIsEqual(currentOptions, lastRecordedOptions)) {
            return;
        }
        setLastRecordedOptions(currentOptions);

        setIsTouched(false);
        if (!specialAutocompleteOnlyToAddMode) {
            // If there’s only one option, just select that automatically.
            if (currentOptions.length === 1) {
                setInputState(optionToValue(currentOptions[0]));
            }
            // Otherwise just reset to the original value.
            // Nick note: it's unclear to me why we like this default behavior of resetting
            //  the input to the reset value on first render. I added this boolean for when our
            //  initial value differs from the reset value and we actually want to start with
            //  the initial value + not update it if options change.
            else if (!disableResetWithoutInput) {
                reset();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOptions]);

    const [autocompleteTypedText, setAutocompleteTypedText] = useState('');
    const matchingOptionValue = useMemo(
        () =>
            currentOptions.find((option) => polymorphicIsEqual(optionToValue(option), value)) ??
            null,
        [currentOptions, optionToValue, value],
    );

    const getOptionToLabel = useCallback(
        (option: Option) => {
            if (isSentinel(option) || option === null) {
                return '';
            }

            return optionToLabel(option);
        },
        [isSentinel, optionToLabel],
    );

    const isOptionEqualToValue = useCallback(
        (option: Option, value: Option) => {
            return isSentinel(option) ? false : optionToValue(option) === optionToValue(value);
        },
        [isSentinel, optionToValue],
    );

    const onChange = useCallback(
        (_event: SyntheticEvent, newValue: Option | NonNullable<Option> | null) => {
            switch (newValue) {
                case SHOW_MORE_SENTINEL:
                    return;
                case null:
                    if (disableClearable) {
                        return;
                    }
                    if (handleNullValue) {
                        handleNullValue();
                    } else {
                        reset();
                    }
                    break;
                default:
                    setInputState(optionToValue(newValue));
            }

            // If we are in `specialAutocompleteOnlyToAddMode`, then as soon as the user selects
            //  an option, we "clear" the text field, so they can select another.
            if (specialAutocompleteOnlyToAddMode) {
                setAutocompleteTypedText('');
            }
        },
        [
            disableClearable,
            handleNullValue,
            optionToValue,
            reset,
            setInputState,
            specialAutocompleteOnlyToAddMode,
        ],
    );

    const filterOptionsProp = useMemo(
        () =>
            typeof filterOptions === 'function'
                ? (optionsList: Option[]) => {
                      const options = filterOptions(optionsList);
                      // Fuzzy matching returns nothing if the input is empty,
                      //  so handle this as a special case.
                      if (!autocompleteTypedText) {
                          return options;
                      }
                      // If the caller wants to filter options, then we have to
                      //  reimplement the fuzzy matching ourselves.
                      return fuzzySearch(
                          autocompleteTypedText,
                          options.map((option) => ({
                              obj: option,
                              key: optionToLabel(option),
                          })),
                      );
                  }
                : undefined,
        [autocompleteTypedText, filterOptions, optionToLabel],
    );

    return (
        <FieldWithAxleLabel
            label={label}
            inlineLabel={inlineLabel}
            tooltipText={tooltipText}
            required={required}
            enabled={enabled}
            disabledHelperText={disabledHelperText}
            sx={style}
            description={description}
        >
            <FormControl variant='outlined' fullWidth>
                <Autocomplete
                    disabled={!enabled}
                    // These control the dropdown options of the filed.
                    options={optionsProp as unknown as Option[]}
                    getOptionLabel={getOptionToLabel}
                    isOptionEqualToValue={isOptionEqualToValue}
                    // These control the “actual value” of the field.
                    value={
                        matchingOptionValue ??
                        (value !== null && displayMissingOptionValue
                            ? // TODO(jessica): This cast represents a limitation around
                              // displaying missing selected values, namely, that we can
                              // only do that when the input value and options are of the
                              // same type. We should improve this in the future.
                              (value as unknown as Option)
                            : null)
                    }
                    onChange={onChange}
                    // These control what text is currently shown as typed in the field.
                    inputValue={autocompleteTypedText}
                    onInputChange={(event, newValue) => setAutocompleteTypedText(newValue)}
                    // Whether to allow clearing the input field (to an empty value)
                    disableClearable={
                        // If we are in `specialAutocompleteOnlyToAddMode`, then it doesn't make sense to "clear"
                        //  the input, since the input itself never has a selected value. Hence, if
                        //  `specialAutocompleteOnlyToAddMode` is true, we force `disableClearable` to be true.
                        specialAutocompleteOnlyToAddMode || disableClearable
                    }
                    // The input component.
                    renderInput={(props) => (
                        <TextField
                            {...props}
                            label={
                                inlineLabel
                                    ? labelWithRequiredAsterisk({ label, required })
                                    : undefined
                            }
                            variant={'outlined'}
                            placeholder={placeholder}
                        />
                    )}
                    size='small'
                    // The options component.
                    // We needed to override this, because the default implementation
                    //  uses the option label as the key, but in the dropdown to select
                    //  QHPs, sometimes different QHPs have the same name, and React complains.
                    //  The real solution is probably making the labels different, as if React
                    //  doesn't know the difference between the options, the user won't either.
                    // TODO: Delete this once we figure out how to distinguish the labels of QHPS
                    //  with the same name.
                    renderOption={(props, option) => {
                        // Render the "Show More" button instead of a regular option
                        if (option === SHOW_MORE_SENTINEL) {
                            return (
                                <Box
                                    key='show-more-button'
                                    sx={{
                                        p: 1.5,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        bgcolor: 'grey.50',
                                    }}
                                >
                                    <Button
                                        fullWidth
                                        size='small'
                                        variant='outlined'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setShowMore(true);
                                        }}
                                    >
                                        {showMoreButtonText} ({moreOptions.length} more)
                                    </Button>
                                </Box>
                            );
                        }
                        if (option === DEFAULT_OPTIONS_SENTINEL) {
                            return (
                                <Box
                                    key='default-options-label'
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        bgcolor: 'grey.50',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Default Options
                                </Box>
                            );
                        }
                        if (option === MORE_OPTIONS_SENTINEL) {
                            return (
                                <Box
                                    key='more-options-label'
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        bgcolor: 'grey.50',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    More Options
                                </Box>
                            );
                        }
                        return (
                            <Box
                                component='li'
                                {...props}
                                key={JSON.stringify(optionToValue(option))}
                                sx={optionStyle}
                            >
                                {renderOption ? renderOption(option) : optionToLabel(option)}
                            </Box>
                        );
                    }}
                    noOptionsText={noOptionsText}
                    // Grouping fields.
                    groupBy={groupBy}
                    renderGroup={renderGroup}
                    // Filtering options workaround.
                    filterOptions={filterOptionsProp}
                    // Cosmetic enhancements.
                    autoComplete={true} // Makes the remainder of the autocompleted option show up in text field.
                    sx={{
                        '& .MuiAutocomplete-inputRoot': { ..._extraAutocompleteStyles },
                        '&& .MuiAutocomplete-inputRoot': { ..._extraAutocompleteStyles },
                    }}
                />
                <If condition={!hideHelperText}>
                    <FormHelperText error={showError}>{helperText}</FormHelperText>
                </If>
            </FormControl>
        </FieldWithAxleLabel>
    );
};
